import type { Selection } from "./markdownCommands";
import { shortcutCommand } from "./markdownEditorToolbar";
import { interceptMentionKeyDown, type useMentionPopup } from "./useMentionPopup";

const NAVIGATION_KEYS = new Set(["ArrowLeft", "ArrowRight", "Home", "End"]);

type EditorHandlerDeps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (() => void) | undefined;
  currentSelection: () => Selection;
  applyResult: (result: { value: string; selection: Selection }) => void;
  popup: ReturnType<typeof useMentionPopup>;
};

export const createEditorHandlers = ({ value, onChange, onSubmit, currentSelection, applyResult, popup }: EditorHandlerDeps) => {
  const handleInput = (event: Event) => {
    const target = event.target as HTMLTextAreaElement;
    onChange(target.value);
    popup.sync(target.value, target.selectionStart);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (interceptMentionKeyDown(event, popup.mentionState, popup.candidates, { navigate: popup.navigate, select: popup.insertMention, close: popup.close })) {
      return;
    }
    const meta = event.metaKey || event.ctrlKey;
    if (meta && event.key === "Enter" && onSubmit) {
      event.preventDefault();
      onSubmit();
      return;
    }
    const command = shortcutCommand(event);
    if (command) {
      event.preventDefault();
      applyResult(command(value, currentSelection()));
    }
  };

  const syncFromTextarea = (textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      popup.sync(textarea.value, textarea.selectionStart);
    }
  };

  const handleKeyUp = (event: KeyboardEvent, textarea: HTMLTextAreaElement | null) => {
    if (NAVIGATION_KEYS.has(event.key)) {
      syncFromTextarea(textarea);
    }
  };

  return { handleInput, handleKeyDown, handleKeyUp, handleClick: syncFromTextarea };
};
